import { useState, useEffect, useRef, useCallback } from 'react'
import { Form } from 'react-bootstrap'
import Radar from 'radar-sdk-js'
import styled from 'styled-components'

export interface RadarAddress {
  number?: string
  street?: string
  city?: string
  state?: string
  stateCode?: string
  postalCode?: string
  country?: string
  countryCode?: string
  formattedAddress?: string
  latitude?: number
  longitude?: number
}

interface RadarAddressAutocompleteProps {
  onAddressSelect: (address: RadarAddress) => void
  value?: string
  placeholder?: string
  disabled?: boolean
}

const AutocompleteWrapper = styled.div`
  position: relative;
  width: 100%;
`

const ResultsDropdown = styled.ul<{ $show: boolean }>`
  display: ${({ $show }) => ($show ? 'block' : 'none')};
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  margin-top: 4px;
  padding: 0;
  list-style: none;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 250px;
  overflow-y: auto;
`

const ResultItem = styled.li<{ $active: boolean }>`
  padding: 0.5rem 1rem;
  font-size: 0.95rem;
  cursor: pointer;
  background: ${({ $active }) => ($active ? '#f8f9fa' : 'white')};
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8f9fa;
  }
`

const RADAR_PUBLISHABLE_KEY = import.meta.env.VITE_RADAR_PUBLISHABLE_KEY

const RadarAddressAutocomplete: React.FC<RadarAddressAutocompleteProps> = ({
  onAddressSelect,
  value = '',
  placeholder = 'Start typing an address...',
  disabled = false,
}) => {
  const [query, setQuery] = useState(value)
  const [results, setResults] = useState<RadarAddress[]>([])
  const [showResults, setShowResults] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [isLoading, setIsLoading] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)
  const initializedRef = useRef(false)

  // Initialize Radar SDK once
  useEffect(() => {
    if (RADAR_PUBLISHABLE_KEY && !initializedRef.current) {
      Radar.initialize(RADAR_PUBLISHABLE_KEY)
      initializedRef.current = true
    }
  }, [])

  // Sync query with external value prop
  useEffect(() => {
    setQuery(value)
  }, [value])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowResults(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const searchAddresses = useCallback(async (searchQuery: string) => {
    if (!RADAR_PUBLISHABLE_KEY || searchQuery.length < 3) {
      setResults([])
      setShowResults(false)
      return
    }

    setIsLoading(true)
    try {
      const response = await Radar.autocomplete({
        query: searchQuery,
        countryCode: 'US',
        layers: ['address'],
        limit: 8,
      })

      if (response.addresses) {
        setResults(response.addresses)
        setShowResults(response.addresses.length > 0)
        setActiveIndex(-1)
      }
    } catch (error) {
      console.error('Radar autocomplete error:', error)
      setResults([])
      setShowResults(false)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setQuery(value)

    // Debounce the API call
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      searchAddresses(value)
    }, 200)
  }

  const handleSelect = (address: RadarAddress) => {
    setQuery(address.formattedAddress || '')
    setShowResults(false)
    setResults([])
    onAddressSelect(address)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showResults || results.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev))
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev))
        break
      case 'Enter':
        e.preventDefault()
        if (activeIndex >= 0 && activeIndex < results.length) {
          handleSelect(results[activeIndex])
        }
        break
      case 'Escape':
        setShowResults(false)
        break
    }
  }

  if (!RADAR_PUBLISHABLE_KEY) {
    return (
      <Form.Control
        type="text"
        placeholder="Address autocomplete not configured"
        disabled
      />
    )
  }

  return (
    <AutocompleteWrapper ref={wrapperRef}>
      <Form.Control
        type="text"
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => results.length > 0 && setShowResults(true)}
        placeholder={isLoading ? 'Searching...' : placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      <ResultsDropdown $show={showResults}>
        {results.map((address, index) => (
          <ResultItem
            key={`${address.formattedAddress}-${index}`}
            $active={index === activeIndex}
            onClick={() => handleSelect(address)}
            onMouseEnter={() => setActiveIndex(index)}
          >
            {address.formattedAddress}
          </ResultItem>
        ))}
      </ResultsDropdown>
    </AutocompleteWrapper>
  )
}

export default RadarAddressAutocomplete
